<template>
  <div class="remarks-widget">
    <!-- INPUT (hidden when isReadOnly) -->
    <div v-if="!isReadOnly" class="remark-composer">
      <div class="composer-header">
        <h3 class="composer-title">Add a remark</h3>
      </div>
      <textarea
        v-model="newRemark"
        :disabled="submitting"
        rows="4"
        maxlength="2000"
        class="composer-input"
        placeholder="Write a note. Use Enter for line breaks."
      ></textarea>
      <div class="composer-footer">
        <span class="composer-counter" :class="{ 'over-limit': newRemark.length > 1900 }">
          {{ newRemark.length }} / 2000
        </span>
        <button
          type="button"
          class="composer-submit"
          :disabled="!canSubmit"
          @click="onSubmit"
        >
          <span v-if="submitting" class="spinner"></span>
          <span v-else>Post</span>
        </button>
      </div>
    </div>

    <!-- HISTORY -->
    <div class="remark-history">
      <div class="history-header">
        <h3 class="history-title">Remark History</h3>
        <span v-if="remarksList && remarksList.length" class="history-counter">
          {{ remarksList.length }}
        </span>
      </div>

      <div v-if="!remarksList || remarksList.length === 0" class="empty-state">
        <div class="empty-illustration">💬</div>
        <p class="empty-title">No remarks yet</p>
        <p v-if="!isReadOnly" class="empty-subtitle">Be the first to add one.</p>
      </div>

      <ul v-else class="remark-list">
        <li
          v-for="r in remarksList"
          :key="r.id"
          class="remark-item"
          :class="{ 'is-own': isOwn(r) }"
        >
          <div
            class="remark-avatar"
            :style="{ backgroundColor: avatarColor(r.profileId) }"
            :title="profileName(r)"
          >
            {{ initials(profileName(r)) }}
          </div>
          <div class="remark-body">
            <div class="remark-head">
              <span class="remark-author">{{ profileName(r) }}</span>
              <span class="remark-date" :title="absoluteDate(r.dateTime)">
                {{ relativeDate(r.dateTime) }}
              </span>
              <button
                v-if="isOwn(r)"
                type="button"
                class="remark-delete"
                title="Delete this remark"
                aria-label="Delete remark"
                @click="askDelete(r)"
              >
                ×
              </button>
            </div>
            <div class="remark-text">{{ r.description }}</div>
          </div>
        </li>
      </ul>
    </div>

    <!-- CONFIRM-DELETE MODAL — reuses CoreUI CModal which the rest of the app uses -->
    <CModal :show.sync="confirmShown" :centered="true">
      <template #header>
        <div class="confirm-header">
          <span class="confirm-icon">⚠️</span>
          <h5 class="confirm-title">Delete this remark?</h5>
        </div>
      </template>
      <div class="confirm-body">
        <p class="confirm-question">This action cannot be undone.</p>
        <blockquote v-if="pendingDelete" class="confirm-preview">
          {{ pendingDelete.description }}
        </blockquote>
      </div>
      <template #footer>
        <button type="button" class="btn-cancel" @click="confirmShown = false" :disabled="deleting">
          Cancel
        </button>
        <button
          type="button"
          class="btn-danger"
          :disabled="deleting"
          @click="confirmDelete"
        >
          <span v-if="deleting" class="spinner"></span>
          <span v-else>Delete</span>
        </button>
      </template>
    </CModal>
  </div>
</template>

<script>
import moment from "moment";
import StockApi from "../../lib/stockApi";
import ApplicationApi from "../../lib/applicationApi";
import RemarkApi from "../../lib/remarkApi";
import helper from "../../lib/helper";

export default {
  name: "WidgetsRemarks",

  props: {
    // Array of remark objects:
    // { id, profileId, description, dateTime, profile: { fullName } }
    remarksList: { type: Array, required: true },

    // 'stock' or 'application' — drives which add-remark API is called.
    entityType: {
      type: String,
      required: true,
      validator: (v) => ["stock", "application"].includes(v),
    },

    // Parent record id (stockId or applicationFormId) — used by the add path.
    entityId: { type: String, required: true },

    // When true, hides the composer. Delete-own is still allowed (so an old
    // typo can be removed even on display-mostly pages).
    isReadOnly: { type: Boolean, default: false },
  },

  data() {
    return {
      newRemark: "",
      submitting: false,
      deleting: false,
      confirmShown: false,
      pendingDelete: null,
      // Resolved from localStorage on mount; if missing (older sessions that
      // pre-date the UserDto.ProfileId addition), we lazily call GET /api/Account
      // and persist the result back to localStorage so the next page load is
      // instant.
      currentProfileId: null,
      stockApi: new StockApi(),
      applicationApi: new ApplicationApi(),
      remarkApi: new RemarkApi(),
    };
  },

  computed: {
    canSubmit() {
      return !this.submitting && this.newRemark.trim().length > 0;
    },
  },

  methods: {
    isOwn(remark) {
      return (
        this.currentProfileId &&
        remark &&
        remark.profileId &&
        remark.profileId === this.currentProfileId
      );
    },

    profileName(remark) {
      return (remark.profile && remark.profile.fullName) || "Unknown";
    },

    initials(name) {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/);
      const first = parts[0] ? parts[0][0] : "";
      const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
      return (first + last).toUpperCase() || name[0].toUpperCase();
    },

    avatarColor(profileId) {
      // Deterministic HSL from the profileId so each user keeps the same
      // avatar colour across the whole app. Saturation + lightness fixed for
      // legibility against white text.
      if (!profileId) return "#9ca3af";
      let hash = 0;
      for (let i = 0; i < profileId.length; i++) {
        hash = (hash << 5) - hash + profileId.charCodeAt(i);
        hash |= 0;
      }
      const hue = Math.abs(hash) % 360;
      return `hsl(${hue}, 55%, 48%)`;
    },

    relativeDate(dt) {
      if (!dt) return "";
      return moment(dt).fromNow();
    },

    absoluteDate(dt) {
      if (!dt) return "";
      return moment(dt).format("DD MMM YYYY, HH:mm");
    },

    onSubmit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      const text = this.newRemark;

      const promise =
        this.entityType === "stock"
          ? this.stockApi.updateRemarks({ stockId: this.entityId, remark: text })
          : this.applicationApi.updateAddRemarks({
              applicationFormId: this.entityId,
              description: text,
            });

      promise
        .then(() => {
          this.newRemark = "";
          this.$emit("added");
          this.$emit("toast", {
            header: "Remark",
            message: "Remark posted.",
            color: "success",
          });
        })
        .catch((err) => {
          const data = err && err.data ? err.data : err;
          this.$emit("toast", {
            header: "Error",
            message: helper.getErrorMessage(data) || "Could not post remark.",
            color: "danger",
          });
        })
        .finally(() => {
          this.submitting = false;
        });
    },

    askDelete(remark) {
      this.pendingDelete = remark;
      this.confirmShown = true;
    },

    confirmDelete() {
      if (!this.pendingDelete) return;
      const id = this.pendingDelete.id;
      this.deleting = true;
      this.remarkApi
        .delete(id)
        .then(() => {
          this.confirmShown = false;
          this.pendingDelete = null;
          this.$emit("deleted", id);
          this.$emit("toast", {
            header: "Remark",
            message: "Remark deleted.",
            color: "success",
          });
        })
        .catch((err) => {
          const data = err && err.data ? err.data : err;
          // 403 surfaces here if a user tries to delete someone else's row
          // via tampered request — friendly message either way.
          this.$emit("toast", {
            header: "Error",
            message: helper.getErrorMessage(data) || "Could not delete remark.",
            color: "danger",
          });
        })
        .finally(() => {
          this.deleting = false;
        });
    },

    // Reads the current user's profileId from localStorage. If it's missing
    // (session predates the UserDto.ProfileId rollout), fetch from GET /api/Account
    // once, persist back to localStorage so future loads skip the network call,
    // and update reactive state so the delete affordance appears without a reload.
    async resolveCurrentProfileId() {
      // Fast path — already in localStorage
      try {
        const raw = window.localStorage.getItem("user");
        if (raw) {
          const u = JSON.parse(raw);
          if (u && u.profileId) {
            this.currentProfileId = u.profileId;
            return;
          }
        }
      } catch (e) {
        // fall through to network fetch
      }

      // Slow path — call /api/Account to refresh the user object
      try {
        const apiUrl =
          (window.config && window.config.API_URL) ||
          process.env.VUE_APP_API_URL ||
          "";
        const token = window.localStorage.getItem("token");
        if (!apiUrl || !token) return;
        const r = await fetch(apiUrl + "Account", {
          method: "GET",
          headers: { Authorization: "Bearer " + token },
        });
        if (!r.ok) return;
        const body = await r.json();
        const dto = body.result || body;
        if (!dto || !dto.profileId) return;
        // Merge back into localStorage so the rest of the app benefits too.
        const existing = JSON.parse(
          window.localStorage.getItem("user") || "{}"
        );
        const merged = { ...existing, ...dto, profileId: dto.profileId };
        window.localStorage.setItem("user", JSON.stringify(merged));
        if (dto.token) window.localStorage.setItem("token", dto.token);
        this.currentProfileId = dto.profileId;
      } catch (e) {
        // Network failure — silently leave currentProfileId null so the delete
        // button stays hidden. Not a blocker for using the rest of the widget.
      }
    },
  },

  mounted() {
    this.resolveCurrentProfileId();
  },
};
</script>

<style scoped>
/* ===== Composer (input) ===== */
.remarks-widget {
  --rw-border: #e5e7eb;
  --rw-text: #111827;
  --rw-muted: #6b7280;
  --rw-accent: #2563eb;
  --rw-danger: #dc2626;
  --rw-bg-soft: #f9fafb;
}

.remark-composer {
  background: white;
  border: 1px solid var(--rw-border);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.composer-header {
  margin-bottom: 10px;
}

.composer-title {
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--rw-text);
  margin: 0;
  letter-spacing: 0.01em;
}

.composer-input {
  width: 100%;
  border: 1px solid var(--rw-border);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--rw-text);
  resize: vertical;
  min-height: 92px;
  background: white;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  font-family: inherit;
}
.composer-input:focus {
  outline: none;
  border-color: var(--rw-accent);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}
.composer-input:disabled {
  background: var(--rw-bg-soft);
  cursor: not-allowed;
}

.composer-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  gap: 10px;
}

.composer-counter {
  font-size: 0.78rem;
  color: var(--rw-muted);
}
.composer-counter.over-limit {
  color: var(--rw-danger);
  font-weight: 600;
}

.composer-submit {
  background: var(--rw-accent);
  color: white;
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 78px;
  justify-content: center;
}
.composer-submit:hover:not(:disabled) {
  background: #1d4ed8;
}
.composer-submit:active:not(:disabled) {
  transform: translateY(1px);
}
.composer-submit:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

/* ===== History list ===== */
.remark-history {
  background: white;
  border: 1px solid var(--rw-border);
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.history-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.history-title {
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--rw-text);
  margin: 0;
  letter-spacing: 0.01em;
}

.history-counter {
  background: var(--rw-bg-soft);
  color: var(--rw-muted);
  font-size: 0.72rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
}

.empty-state {
  text-align: center;
  padding: 28px 12px;
  color: var(--rw-muted);
}
.empty-illustration {
  font-size: 2.2rem;
  margin-bottom: 6px;
  opacity: 0.6;
}
.empty-title {
  font-weight: 600;
  margin: 0;
  color: #4b5563;
}
.empty-subtitle {
  margin: 4px 0 0;
  font-size: 0.85rem;
}

.remark-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.remark-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 10px;
  background: var(--rw-bg-soft);
  border: 1px solid transparent;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.remark-item.is-own {
  background: #eff6ff;
  border-color: #dbeafe;
}
.remark-item.is-own:hover {
  background: #dbeafe;
}

.remark-avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: white;
  font-weight: 700;
  font-size: 0.82rem;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  letter-spacing: 0.02em;
}

.remark-body {
  flex: 1;
  min-width: 0;
}

.remark-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.remark-author {
  font-weight: 700;
  color: var(--rw-text);
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.remark-date {
  font-size: 0.78rem;
  color: var(--rw-muted);
  cursor: help;
  flex-shrink: 0;
}

.remark-delete {
  margin-left: auto;
  background: transparent;
  border: none;
  color: var(--rw-muted);
  font-size: 1.2rem;
  line-height: 1;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.55;
  transition: background 0.15s ease, color 0.15s ease, opacity 0.15s ease;
  flex-shrink: 0;
}
.remark-delete:hover {
  background: #fee2e2;
  color: var(--rw-danger);
  opacity: 1;
}

.remark-text {
  font-size: 0.92rem;
  color: var(--rw-text);
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

/* ===== Confirm modal ===== */
.confirm-header {
  display: flex;
  align-items: center;
  gap: 10px;
}
.confirm-icon {
  font-size: 1.4rem;
}
.confirm-title {
  margin: 0;
  font-weight: 700;
  font-size: 1.05rem;
}
.confirm-body {
  padding: 4px 0;
}
.confirm-question {
  margin: 0 0 12px;
  color: var(--rw-muted);
}
.confirm-preview {
  background: var(--rw-bg-soft);
  border-left: 3px solid #cbd5e1;
  margin: 0;
  padding: 10px 12px;
  font-size: 0.88rem;
  color: #374151;
  white-space: pre-wrap;
  max-height: 160px;
  overflow-y: auto;
  border-radius: 0 6px 6px 0;
}

.btn-cancel,
.btn-danger {
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
}
.btn-cancel {
  background: #e5e7eb;
  color: #374151;
  margin-right: 8px;
}
.btn-cancel:hover:not(:disabled) {
  background: #d1d5db;
}
.btn-danger {
  background: var(--rw-danger);
  color: white;
  min-width: 88px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.btn-danger:hover:not(:disabled) {
  background: #b91c1c;
}
.btn-danger:disabled,
.btn-cancel:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Spinner used inside Post / Delete buttons during in-flight requests */
.spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: white;
  border-radius: 50%;
  animation: rw-spin 0.7s linear infinite;
}
@keyframes rw-spin {
  to { transform: rotate(360deg); }
}

/* ===== Mobile ===== */
@media (max-width: 575.98px) {
  .remark-composer,
  .remark-history {
    padding: 12px;
    border-radius: 10px;
  }
  .remark-item {
    padding: 10px;
  }
  .remark-avatar {
    width: 32px;
    height: 32px;
    font-size: 0.75rem;
  }
  .remark-head {
    flex-wrap: wrap;
  }
  .remark-delete {
    opacity: 0.8; /* always-visible on touch */
  }
}
</style>
